import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import {
  useIsAuthenticated,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';

import { loginRequest } from './auth/msalConfig';
import { AppNav } from './components/AppNav';
import { GlobalStateStyles } from './components/GlobalStateStyles';
import { StateSettingsProvider } from './api/StateSettingsContext';
import { FrameworkProvider } from './framework';

/**
 * App shell — header (brand + language toggle + sign in/out), routed body
 * via <Outlet />, and footer. Authentication gating wraps the routed body;
 * unauthenticated users see the welcome copy and sign-in prompt instead.
 *
 * Route table lives in main.tsx (data router posture, createBrowserRouter).
 * Routes that render here:
 *   /                — HomeView (kaimahi landing: whakataukī, stats,
 *                      action cards, recent caseload).
 *   /directory       — DirectoryView (search + sort + whānau list).
 *   /whanau/:id      — WhanauProfile (per-whānau read + edit; useBlocker
 *                      protects against unsaved-change loss).
 *
 * See docs/ADR-001-platform-architecture.md for the broader architecture
 * and docs/ADR-006-access-control-model.md §5b for the role override model.
 */
function App() {
  const { t, i18n } = useTranslation();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // First-kupu of the kaimahi's display name — feels warmer in the header
  // greeting than the full formal name. Falls back to the whole string if
  // there are no spaces or no name is available.
  const account = isAuthenticated ? instance.getActiveAccount() : null;
  const displayName = account?.name ?? account?.username ?? '';
  const firstName = displayName.split(' ')[0] ?? displayName;

  const toggleLanguage = () => {
    const next = i18n.language === 'mi' ? 'en' : 'mi';
    void i18n.changeLanguage(next);
    document.documentElement.lang = next;
  };

  const handleSignIn = () => {
    instance.loginPopup(loginRequest).catch((error: unknown) => {
      console.error('[WFP] Sign-in failed:', error);
    });
  };

  return (
    <main className="shell">
      <header className="shell__header">
        {isAuthenticated ? (
          <Link to="/" className="brand brand--link">
            <span className="brand__mark" aria-hidden="true" />
            <div>
              <p className="brand__org">Te Tohu o Te Ora o Ngāti Awa</p>
              <h1 className="brand__title">{t('app.title')}</h1>
            </div>
          </Link>
        ) : (
          <div className="brand">
            <span className="brand__mark" aria-hidden="true" />
            <div>
              <p className="brand__org">Te Tohu o Te Ora o Ngāti Awa</p>
              <h1 className="brand__title">{t('app.title')}</h1>
            </div>
          </div>
        )}
        <div className="shell__actions">
          {isAuthenticated && firstName && (
            <span className="shell__greeting">
              {t('home.greeting', { name: firstName })}
            </span>
          )}
          <UnauthenticatedTemplate>
            <button
              type="button"
              className="lang-toggle"
              onClick={toggleLanguage}
              aria-label={t('app.toggleLanguage')}
            >
              {i18n.language === 'mi' ? 'English' : 'Te Reo Māori'}
            </button>
            <button
              type="button"
              className="auth-button auth-button--primary"
              onClick={handleSignIn}
            >
              {t('auth.signIn')}
            </button>
          </UnauthenticatedTemplate>
        </div>
      </header>

      <AuthenticatedTemplate>
        <StateSettingsProvider>
          <FrameworkProvider>
            <GlobalStateStyles />
            <AppNav />
          </FrameworkProvider>
        </StateSettingsProvider>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <section className="welcome">
          <p className="welcome__greeting">{t('app.greeting')}</p>
          <p className="welcome__subtitle">{t('app.subtitle')}</p>
          <p className="welcome__pou">
            <span lang="mi">Te Pou Ihorangi</span>
            <span aria-hidden="true"> · </span>
            <span lang="mi">Te Pou o Hineahuone</span>
            <span aria-hidden="true"> · </span>
            <span lang="mi">Te Pou Aotūroa</span>
            <span aria-hidden="true"> · </span>
            <span lang="mi">Te Pou Hinetītama</span>
          </p>
        </section>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <StateSettingsProvider>
          <FrameworkProvider>
            <section className="app-body">
              <Outlet />
            </section>
          </FrameworkProvider>
        </StateSettingsProvider>
      </AuthenticatedTemplate>

      <footer className="shell__footer">
        <p>{t('footer.kaupapa')}</p>
      </footer>
    </main>
  );
}

export default App;
